"use client";

import { useEffect, useRef } from "react";
import {
  BRIEF_DEEP_LINK_DATE_PARAM,
  BRIEF_DEEP_LINK_PARAM,
} from "@/lib/reports/deep-link";
import type { ExploreBriefCard } from "../types";

/**
 * Remove the brief deep-link params from the URL (without a navigation) once
 * the deep-linked dialog has been closed, so refresh / history restore does
 * not reopen it.
 */
export function clearBriefDeepLinkParams(): void {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has(BRIEF_DEEP_LINK_PARAM)) {
    return;
  }
  url.searchParams.delete(BRIEF_DEEP_LINK_PARAM);
  url.searchParams.delete(BRIEF_DEEP_LINK_DATE_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

/**
 * Consume `?brief=<briefId>&bd=<reportDate>` — emitted by newsletter emails
 * (`buildBriefDeepLink`) and the brief dialogs' share buttons — and open the
 * matching brief's dialog on mount.
 *
 * Reads `window.location.search` directly rather than `useSearchParams`:
 * the explore pages are `force-static`, where Next's hook returns empty
 * params, and a deep link always arrives as a full document load.
 *
 * `bd` guards against briefId reuse: briefIds are only unique within one
 * (reportDate, reportType) edition, so when the param is present it must
 * match the brief's own reportDate. A miss (stale email, wrong date) is a
 * silent no-op — the user simply lands on the page.
 */
export function useBriefDeepLink({
  briefs,
  onOpen,
}: {
  briefs: ExploreBriefCard[];
  onOpen: (brief: ExploreBriefCard) => void;
}): void {
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const briefId = params.get(BRIEF_DEEP_LINK_PARAM);
    if (!briefId) {
      consumedRef.current = true;
      return;
    }
    const briefDate = params.get(BRIEF_DEEP_LINK_DATE_PARAM);
    const match = briefs.find(
      (brief) =>
        brief.briefId === briefId &&
        (!briefDate || brief.reportDate === briefDate)
    );
    if (!match) {
      return;
    }
    consumedRef.current = true;
    onOpen(match);
  }, [briefs, onOpen]);
}
